import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrTrees } from '@/api/ivrTrees';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { routeRef } from '@/lib/formatRef';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/**
 * Create a fresh IVR tree. For super_admin / reseller_owner the
 * `useCustomerPicker` hook renders a customer dropdown (hidden for
 * office_manager, whose tenant scope already fixes the customer).
 *
 * On success the user is navigated straight into the tree editor so
 * they can apply a template / start adding nodes.
 */
export function CreateTreeDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: () =>
      ivrTrees.create({
        name: name.trim(),
        // Send customer_id only when the picker is visible — for
        // office_manager the backend derives it from tenant scope.
        ...(effectiveCustomerId
          ? { customer_id: effectiveCustomerId }
          : {}),
      }),
    onSuccess: (tree) => {
      qc.invalidateQueries({ queryKey: ['ivrTrees'] });
      onOpenChange(false);
      resetForm();
      navigate(`/ivr/trees/${routeRef(tree, 'IVT')}`);
    },
  });

  function resetForm() {
    setName('');
    setCustomerId(null);
    setFieldErrors({});
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    const trimmed = name.trim();
    if (!trimmed) errs.name = 'Name is required.';
    else if (trimmed.length > 200) errs.name = 'Name must be 200 characters or fewer.';
    if (isPickerVisible && !effectiveCustomerId) errs.customer = 'Customer is required.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    create.mutate();
  }

  const canSubmit =
    !create.isPending &&
    name.trim().length > 0 &&
    (!isPickerVisible || !!effectiveCustomerId);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!create.isPending) {
          onOpenChange(v);
          if (!v) resetForm();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New IVR tree</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* When a super_admin / reseller_owner has entered a customer via
              the customers list, the picker collapses and we surface the
              entered customer as a read-only chip for confidence. Legacy
              path (no entered context) still renders the full picker. */}
          <CustomerScopeChip />
          {customerPicker}
          {fieldErrors.customer && (
            <p role="alert" className="-mt-2 text-xs text-red-500">
              {fieldErrors.customer}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="tree-name">
              Name <span aria-hidden className="text-red-500">*</span>
            </Label>
            <Input
              id="tree-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Main Phone Tree"
              autoFocus
              autoComplete="off"
              maxLength={200}
            />
            {fieldErrors.name && (
              <p role="alert" className="text-xs text-red-500">{fieldErrors.name}</p>
            )}
            <p className="text-[11px] text-muted-foreground">
              You can apply a template (3-option menu, voicemail greeting, business hours)
              after the tree is created.
            </p>
          </div>

          {create.isError && (
            <p role="alert" className="text-xs text-red-500">
              {create.error instanceof Error ? create.error.message : 'Failed to create tree.'}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={create.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create tree'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
